function isHex(value) {
  return /^[0-9a-f]+$/i.test(value);
}

/**
 * Safely reads a fixed-width hexadecimal field from a device protocol string.
 */
export function readHexInt(source, start, length, fallback = 0) {
  if (typeof source !== "string" || start < 0 || length <= 0) {
    return fallback;
  }

  const segment = source.slice(start, start + length);
  if (segment.length !== length || !isHex(segment)) {
    return fallback;
  }

  const value = parseInt(segment, 16);
  return Number.isNaN(value) ? fallback : value;
}

export function readScaledHex(
  source,
  start,
  length,
  scale = 1,
  digits = 0,
  fallback = 0
) {
  const value = readHexInt(source, start, length, null);
  if (value === null || !scale) {
    return fallback;
  }

  return Number((value / scale).toFixed(digits));
}

/**
 * Reads a protocol counter whose hexadecimal characters are stored in reverse
 * order. This follows the device protocol example: 29800000 -> 00000892.
 */
export function readProtocolReversedHexInt(
  source,
  start,
  length,
  fallback = 0
) {
  if (typeof source !== "string" || start < 0 || length <= 0) {
    return fallback;
  }

  const segment = source.slice(start, start + length);
  if (segment.length !== length || !isHex(segment)) {
    return fallback;
  }

  const value = parseInt(segment.split("").reverse().join(""), 16);
  return Number.isNaN(value) ? fallback : value;
}

export function readProtocolReversedHexScaled(
  source,
  start,
  length,
  scale = 1,
  digits = 0,
  fallback = 0
) {
  const value = readProtocolReversedHexInt(source, start, length, null);
  if (value === null || !scale) {
    return fallback;
  }

  return Number((value / scale).toFixed(digits));
}

export function isBitSet(value, bit) {
  return Number.isFinite(value) && (value & (1 << bit)) !== 0;
}

export function splitProtocolGroups(
  source,
  width,
  { radix = 16, scale = 1, digits = null } = {}
) {
  if (typeof source !== "string" || width <= 0) {
    return [];
  }

  const values = [];
  const validLength = source.length - (source.length % width);
  const isValidGroup =
    radix === 16 ? isHex : segment => /^\d+$/.test(segment);

  for (let index = 0; index < validLength; index += width) {
    const segment = source.slice(index, index + width);
    if (!isValidGroup(segment)) {
      continue;
    }

    const value = parseInt(segment, radix);
    if (Number.isNaN(value)) {
      continue;
    }

    const scaledValue = value / scale;
    values.push(
      digits === null ? scaledValue : Number(scaledValue.toFixed(digits))
    );
  }

  return values;
}
